import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
	ArrowLeft,
	Building2,
	Calendar,
	Clock,
	DollarSign,
	Download,
	Edit,
	FileText,
	Loader2,
	RefreshCcw,
	Upload,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/layouts/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useTRPC } from "@/integrations/trpc/react";
import { useAuth } from "@/lib/auth";
import { getAwardStatusColor } from "@/lib/utils";

export const Route = createFileRoute("/awards/$id")({
	component: AwardDetailPage,
});

function AwardDetailPage() {
	const { id } = Route.useParams();
	const { hasPermission, user } = useAuth();
	const trpc = useTRPC();
	const [activeTab, setActiveTab] = useState("overview");
	const [extensionDialogOpen, setExtensionDialogOpen] = useState(false);
	const [editDialogOpen, setEditDialogOpen] = useState(false);
	const [extensionForm, setExtensionForm] = useState({
		newEndDate: "",
		reason: "",
		isExtension: true,
	});
	const [editForm, setEditForm] = useState({
		contractStartDate: "",
		contractEndDate: "",
		isExtension: false,
	});

	// Query the award by ID
	const { data: award, isLoading: awardLoading } = useQuery(
		trpc.awards.bids.get.queryOptions({ awardId: id }),
	);

	// Query related solicitation for title
	const { data: solicitation } = useQuery(
		trpc.solicitations.get.queryOptions({ id: award?.solicitation_id ?? "" }),
	);

	// Query contract documents
	const { data: contractDocs } = useQuery(
		trpc.awards.documents.list.queryOptions({
			solicitationId: award?.solicitation_id ?? "",
			buyerOrgId: user?.organizationId ?? "",
		}),
	);

	// Query contract dates (including extensions)
	const { data: contractDates } = useQuery(
		trpc.awards.contractDates.list.queryOptions({
			solicitationId: award?.solicitation_id ?? "",
			buyerOrgId: user?.organizationId ?? "",
		}),
	);

	// Build extension history from contract dates with is_extension flag
	const extensions = (contractDates ?? [])
		.filter((d) => d.is_extension)
		.map((d, idx) => ({
			id: d.id,
			previousEndDate: d.original_end_date ?? d.start_date,
			newEndDate: d.end_date,
			reason: d.extension_notes ?? "Extension approved",
			approvedBy: d.adjusted_by ?? "System",
			approvedAt: d.adjusted_at ?? d.created_at ?? "",
		}));

	const handleExtensionSubmit = () => {
		toast.success("Contract extended successfully!");
		setExtensionDialogOpen(false);
		setExtensionForm({ newEndDate: "", reason: "", isExtension: true });
	};

	// Get the primary contract dates (first period or most recent)
	const primaryContractDate = (contractDates ?? [])[0];
	const contractStartDate = primaryContractDate?.start_date ?? award?.awarded_at ?? "";
	const contractEndDate = primaryContractDate?.end_date ?? "";

	const handleOpenEditDialog = () => {
		setEditForm({
			contractStartDate: contractStartDate,
			contractEndDate: contractEndDate,
			isExtension: false,
		});
		setEditDialogOpen(true);
	};

	const handleEditSubmit = () => {
		const message = editForm.isExtension
			? "Contract extended successfully!"
			: "Contract details updated successfully!";
		toast.success(message);
		setEditDialogOpen(false);
	};

	if (awardLoading) {
		return (
			<DashboardLayout>
				<div className="flex items-center justify-center h-64">
					<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
				</div>
			</DashboardLayout>
		);
	}

	if (!award) {
		return (
			<DashboardLayout>
				<div className="text-center py-12">
					<h2 className="text-2xl font-bold">Award not found</h2>
					<Button asChild className="mt-4">
						<Link to="/awards">Back to Awards</Link>
					</Button>
				</div>
			</DashboardLayout>
		);
	}

	// Calculate contract metrics
	const startDate = new Date(contractStartDate || award.awarded_at);
	const endDate = contractEndDate ? new Date(contractEndDate) : new Date(startDate.getTime() + 365 * 24 * 60 * 60 * 1000);
	const contractDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
	const daysRemaining = Math.ceil((endDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));

	// Map contract documents
	const documents = (contractDocs ?? []).map((doc) => ({
		name: doc.title ?? doc.original_filename ?? "Document",
		date: doc.created_at ?? award.awarded_at,
		type: doc.document_type ?? "supporting",
	}));

	// Determine award status based on contract dates
	const awardStatus = contractEndDate && new Date(contractEndDate) < new Date() ? "completed" : "active";

	return (
		<DashboardLayout>
			<div className="space-y-6">
				{/* Header */}
				<div className="flex items-start justify-between">
					<div className="space-y-1">
						<div className="flex items-center gap-2">
							<Button variant="ghost" size="icon" asChild>
								<Link to="/awards">
									<ArrowLeft className="h-4 w-4" />
								</Link>
							</Button>
							<Badge className={getAwardStatusColor(awardStatus)}>
								{awardStatus}
							</Badge>
						</div>
						<h1 className="text-3xl font-bold tracking-tight">
							{solicitation?.title ?? "Award"}
						</h1>
						<div className="flex items-center gap-2 text-muted-foreground">
							<span>{award.vendor_name ?? "Unknown Vendor"}</span>
							{award.vendor_display_email && (
								<>
									<span>•</span>
									<span>{award.vendor_display_email}</span>
								</>
							)}
							<span>•</span>
							<Link
								to="/solicitations/$id"
								params={{ id: award.solicitation_id }}
								className="text-primary hover:underline"
							>
								View Solicitation
							</Link>
						</div>
					</div>
					{hasPermission("edit") && (
						<div className="flex gap-2">
							<Button variant="outline" onClick={handleOpenEditDialog}>
								<Edit className="mr-2 h-4 w-4" />
								Edit
							</Button>
						</div>
					)}
				</div>

				{/* Quick Stats */}
				<div className="grid gap-4 md:grid-cols-4">
					<Card>
						<CardContent className="pt-6">
							<div className="flex items-center gap-2">
								<Building2 className="h-4 w-4 text-muted-foreground" />
								<span className="text-sm text-muted-foreground">Vendor</span>
							</div>
							<p className="text-lg font-bold mt-1">
								{award.vendor_name ?? "Unknown Vendor"}
							</p>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="pt-6">
							<div className="flex items-center gap-2">
								<DollarSign className="h-4 w-4 text-muted-foreground" />
								<span className="text-sm text-muted-foreground">
									Award Date
								</span>
							</div>
							<p className="text-2xl font-bold mt-1">
								{award.awarded_at ? new Date(award.awarded_at).toLocaleDateString() : "-"}
							</p>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="pt-6">
							<div className="flex items-center gap-2">
								<Calendar className="h-4 w-4 text-muted-foreground" />
								<span className="text-sm text-muted-foreground">
									Contract Length
								</span>
							</div>
							<p className="text-2xl font-bold mt-1">{contractDays > 0 ? `${contractDays} days` : "-"}</p>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="pt-6">
							<div className="flex items-center gap-2">
								<Clock className="h-4 w-4 text-muted-foreground" />
								<span className="text-sm text-muted-foreground">
									Days Remaining
								</span>
							</div>
							<p
								className={`text-2xl font-bold mt-1 ${daysRemaining < 30 && daysRemaining > 0 ? "text-red-600" : ""}`}
							>
								{contractEndDate ? (daysRemaining > 0 ? daysRemaining : "Expired") : "-"}
							</p>
						</CardContent>
					</Card>
				</div>

				{/* Tabs */}
				<Tabs value={activeTab} onValueChange={setActiveTab}>
					<TabsList>
						<TabsTrigger value="overview">Overview</TabsTrigger>
						<TabsTrigger value="documents">Documents</TabsTrigger>
						<TabsTrigger value="extensions">
							Extensions ({extensions.length})
						</TabsTrigger>
						<TabsTrigger value="timeline">Timeline</TabsTrigger>
					</TabsList>

					<TabsContent value="overview" className="space-y-6">
						<div className="grid gap-6 md:grid-cols-2">
							<Card>
								<CardHeader>
									<CardTitle>Contract Details</CardTitle>
								</CardHeader>
								<CardContent className="space-y-4">
									<div>
										<p className="text-sm text-muted-foreground">Award Date</p>
										<p className="font-medium">
											{award.awarded_at ? new Date(award.awarded_at).toLocaleDateString() : "-"}
										</p>
									</div>
									<Separator />
									<div>
										<p className="text-sm text-muted-foreground">
											Contract Start
										</p>
										<p className="font-medium">
											{contractStartDate ? new Date(contractStartDate).toLocaleDateString() : "-"}
										</p>
									</div>
									<Separator />
									<div>
										<p className="text-sm text-muted-foreground">
											Contract End
										</p>
										<p className="font-medium">
											{contractEndDate ? new Date(contractEndDate).toLocaleDateString() : "-"}
										</p>
									</div>
									{award.notes && (
										<>
											<Separator />
											<div>
												<p className="text-sm text-muted-foreground">
													Notes
												</p>
												<p className="font-medium">{award.notes}</p>
											</div>
										</>
									)}
								</CardContent>
							</Card>

							<Card>
								<CardHeader>
									<CardTitle>Contract Progress</CardTitle>
								</CardHeader>
								<CardContent>
									<div className="space-y-4">
										<div>
											<div className="flex justify-between mb-2">
												<span className="text-sm text-muted-foreground">
													Time Elapsed
												</span>
												<span className="text-sm font-medium">
													{Math.round(
														((contractDays - daysRemaining) / contractDays) *
															100,
													)}
													%
												</span>
											</div>
											<div className="h-2 bg-muted rounded-full overflow-hidden">
												<div
													className="h-full bg-primary rounded-full"
													style={{
														width: `${Math.min(100, Math.round(((contractDays - daysRemaining) / contractDays) * 100))}%`,
													}}
												/>
											</div>
										</div>
										<div className="pt-4">
											<p className="text-sm text-muted-foreground mb-2">
												Extension Options
											</p>
											{hasPermission("edit") ? (
												<Button
													variant="outline"
													onClick={() => setExtensionDialogOpen(true)}
												>
													<RefreshCcw className="mr-2 h-4 w-4" />
													Extend
												</Button>
											) : (
												<p className="text-sm">
													No extension options available
												</p>
											)}
										</div>
									</div>
								</CardContent>
							</Card>
						</div>
					</TabsContent>

					<TabsContent value="documents">
						<Card>
							<CardHeader>
								<CardTitle>Contract Documents</CardTitle>
								<CardDescription>
									Award letter, contract, and supporting documents
								</CardDescription>
							</CardHeader>
							<CardContent>
								<div className="space-y-2">
									{documents.map((doc, index) => (
										<div
											key={index}
											className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50"
										>
											<div className="flex items-center gap-3">
												<FileText className="h-5 w-5 text-muted-foreground" />
												<div>
													<p className="font-medium">{doc.name}</p>
													<p className="text-sm text-muted-foreground">
														Uploaded: {new Date(doc.date).toLocaleDateString()}
													</p>
												</div>
											</div>
											<Button
												variant="ghost"
												size="sm"
												onClick={() =>
													toast.success(`Downloading ${doc.name}...`)
												}
											>
												<Download className="h-4 w-4" />
											</Button>
										</div>
									))}
								</div>
							</CardContent>
						</Card>
					</TabsContent>

					<TabsContent value="extensions">
						<Card>
							<CardHeader className="flex flex-row items-center justify-between">
								<div>
									<CardTitle>Contract Extensions</CardTitle>
									<CardDescription>
										Extension history and option year documentation
									</CardDescription>
								</div>
								{hasPermission("edit") && (
									<Button onClick={() => setExtensionDialogOpen(true)}>
										<RefreshCcw className="mr-2 h-4 w-4" />
										Extend
									</Button>
								)}
							</CardHeader>
							<CardContent>
								{extensions.length > 0 ? (
									<div className="space-y-4">
										{extensions.map((ext, index) => (
											<div key={ext.id} className="border rounded-lg p-4">
												<div className="flex items-start justify-between mb-3">
													<div>
														<Badge variant="outline" className="mb-2">
															Extension #{index + 1}
														</Badge>
														<h4 className="font-medium">
															Contract Period Extended
														</h4>
													</div>
													<p className="text-sm text-muted-foreground">
														{new Date(ext.approvedAt).toLocaleDateString()}
													</p>
												</div>
												<div className="grid gap-4 md:grid-cols-2 text-sm">
													<div>
														<span className="text-muted-foreground">
															Previous End Date:
														</span>
														<p className="font-medium">
															{new Date(
																ext.previousEndDate,
															).toLocaleDateString()}
														</p>
													</div>
													<div>
														<span className="text-muted-foreground">
															New End Date:
														</span>
														<p className="font-medium">
															{new Date(ext.newEndDate).toLocaleDateString()}
														</p>
													</div>
												</div>
												<div className="mt-3">
													<span className="text-sm text-muted-foreground">
														Reason:
													</span>
													<p className="text-sm">{ext.reason}</p>
												</div>
												<p className="text-xs text-muted-foreground mt-2">
													Approved by {ext.approvedBy}
												</p>
											</div>
										))}
									</div>
								) : (
									<div className="text-center py-8 text-muted-foreground">
										<RefreshCcw className="h-12 w-12 mx-auto mb-3 opacity-50" />
										<p>No extensions have been requested for this contract.</p>
									</div>
								)}
							</CardContent>
						</Card>
					</TabsContent>

					<TabsContent value="timeline">
						<Card>
							<CardHeader>
								<CardTitle>Award Timeline</CardTitle>
								<CardDescription>
									History of actions on this award
								</CardDescription>
							</CardHeader>
							<CardContent>
								<div className="space-y-4">
									{[
										contractStartDate && {
											action: "Contract started",
											date: contractStartDate,
											user: "System",
										},
										{
											action: "Vendor selected for award",
											date: award.awarded_at ?? "",
											user: award.awarded_by ?? "System",
										},
									]
										.filter((e): e is { action: string; date: string; user: string } => Boolean(e) && Boolean(e.date))
										.map((event, index, arr) => (
											<div key={index} className="flex items-start gap-4">
												<div className="flex flex-col items-center">
													<div className="w-2 h-2 bg-primary rounded-full mt-2" />
													{index < arr.length - 1 && (
														<div className="w-0.5 h-full bg-border flex-1 mt-1" />
													)}
												</div>
												<div className="flex-1 pb-4">
													<p className="font-medium">{event.action}</p>
													<p className="text-sm text-muted-foreground">
														{event.user} -{" "}
														{new Date(event.date).toLocaleDateString()}
													</p>
												</div>
											</div>
										))}
									{!award.awarded_at && (
										<p className="text-center text-muted-foreground py-4">
											No timeline events available
										</p>
									)}
								</div>
							</CardContent>
						</Card>
					</TabsContent>
				</Tabs>

				{/* Extension Dialog */}
				<Dialog
					open={extensionDialogOpen}
					onOpenChange={setExtensionDialogOpen}
				>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>Extend Contract</DialogTitle>
							<DialogDescription>
								Extend the contract end date and provide documentation.
							</DialogDescription>
						</DialogHeader>
						<div className="space-y-4 py-4">
							<div className="space-y-2">
								<Label htmlFor="newEndDate">New End Date</Label>
								<Input
									id="newEndDate"
									type="date"
									value={extensionForm.newEndDate}
									onChange={(e) =>
										setExtensionForm((prev) => ({
											...prev,
											newEndDate: e.target.value,
										}))
									}
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="reason">Reason for Extension</Label>
								<Textarea
									id="reason"
									value={extensionForm.reason}
									onChange={(e) =>
										setExtensionForm((prev) => ({
											...prev,
											reason: e.target.value,
										}))
									}
									placeholder="Describe the reason for this extension..."
									rows={3}
								/>
							</div>
							<div className="flex items-center space-x-2">
								<Checkbox
									id="isExtension"
									checked={extensionForm.isExtension}
									onCheckedChange={(checked) =>
										setExtensionForm((prev) => ({
											...prev,
											isExtension: checked as boolean,
										}))
									}
								/>
								<Label htmlFor="isExtension" className="font-normal">
									This is a contract extension (vs. date correction)
								</Label>
							</div>
							<div className="border-2 border-dashed rounded-lg p-4 text-center">
								<Upload className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
								<p className="text-sm text-muted-foreground">
									Upload extension documentation (optional)
								</p>
								<Button variant="outline" size="sm" className="mt-2">
									Select File
								</Button>
							</div>
						</div>
						<DialogFooter>
							<Button
								variant="outline"
								onClick={() => setExtensionDialogOpen(false)}
							>
								Cancel
							</Button>
							<Button onClick={handleExtensionSubmit}>Extend Contract</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>

				{/* Edit Contract Dialog */}
				<Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>Edit Contract</DialogTitle>
							<DialogDescription>
								Update contract dates and details.
							</DialogDescription>
						</DialogHeader>
						<div className="space-y-4 py-4">
							<div className="space-y-2">
								<Label htmlFor="editContractStartDate">
									Contract Start Date
								</Label>
								<Input
									id="editContractStartDate"
									type="date"
									value={editForm.contractStartDate}
									onChange={(e) =>
										setEditForm((prev) => ({
											...prev,
											contractStartDate: e.target.value,
										}))
									}
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="editContractEndDate">Contract End Date</Label>
								<Input
									id="editContractEndDate"
									type="date"
									value={editForm.contractEndDate}
									onChange={(e) =>
										setEditForm((prev) => ({
											...prev,
											contractEndDate: e.target.value,
										}))
									}
								/>
							</div>
							{editForm.contractEndDate !== contractEndDate && (
								<>
									<div className="flex items-center space-x-2">
										<input
											type="checkbox"
											id="editIsExtension"
											checked={editForm.isExtension}
											onChange={(e) =>
												setEditForm((prev) => ({
													...prev,
													isExtension: e.target.checked,
												}))
											}
											className="h-4 w-4 rounded border-gray-300"
										/>
										<Label htmlFor="editIsExtension" className="font-normal">
											This is a contract extension
										</Label>
									</div>
									{editForm.isExtension && (
										<div className="border-2 border-dashed rounded-lg p-4 text-center">
											<Upload className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
											<p className="text-sm text-muted-foreground">
												Upload extension documentation (optional)
											</p>
											<Button
												variant="outline"
												size="sm"
												className="mt-2"
												onClick={() =>
													toast.success("Extension document uploaded!")
												}
											>
												Select File
											</Button>
										</div>
									)}
								</>
							)}
						</div>
						<DialogFooter>
							<Button
								variant="outline"
								onClick={() => setEditDialogOpen(false)}
							>
								Cancel
							</Button>
							<Button onClick={handleEditSubmit}>Save Changes</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			</div>
		</DashboardLayout>
	);
}
